// Shared iOS-style form primitives for the per-kind attribute edit
// sheets (P1-18 PR B). Pulled into a sibling so AmmoAttrsSheet /
// OpticsAttrsSheet / HolsterAttrsSheet share one implementation.
//
// Primitives:
//   Field        — label + inset bg + child input
//   TextInput    — managed text input, trims on commit
//   NumberInput  — numeric input (inputMode="numeric"); null when empty
//   Toggle       — iOS switch (true/false)
//   Segmented    — N-way enum picker (also handles optional via "None")
//
// All controls are plain React — no third-party dependency.

import type { ReactNode } from "react";

export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string | null;
}) {
  // Field wraps a visual label around whatever control the caller passes.
  // Biome's a11y rule can't statically see the inner control, so suppress.
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: children slot carries the control
    <label className="block">
      <span className="block text-[13px] font-medium text-slate-500 mb-1">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[12px] text-[#ff3b30]">{hint}</span>}
    </label>
  );
}

export function TextInput({
  value,
  onChange,
  disabled,
  placeholder,
  ariaInvalid,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
  ariaInvalid?: boolean;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-invalid={ariaInvalid}
      className="w-full h-11 rounded-lg bg-slate-100 px-3 text-[17px] text-slate-900 outline-none focus:ring-2 focus:ring-[#007aff]/40 disabled:opacity-60"
      placeholder={placeholder ?? ""}
    />
  );
}

export function NumberInput({
  value,
  onChange,
  disabled,
  placeholder,
  ariaInvalid,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
  ariaInvalid?: boolean;
}) {
  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={value === null ? "" : String(value)}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^0-9]/g, "");
        if (raw === "") onChange(null);
        else onChange(Number.parseInt(raw, 10));
      }}
      disabled={disabled}
      aria-invalid={ariaInvalid}
      className="w-full h-11 rounded-lg bg-slate-100 px-3 text-[17px] text-slate-900 tabular-nums outline-none focus:ring-2 focus:ring-[#007aff]/40 disabled:opacity-60"
      placeholder={placeholder ?? ""}
    />
  );
}

export function Toggle({
  value,
  onChange,
  disabled,
  label,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-[31px] w-[51px] rounded-full transition shrink-0 ${
        value ? "bg-[#34c759]" : "bg-slate-300"
      } disabled:opacity-50`}
    >
      <span
        className={`absolute top-[2px] h-[27px] w-[27px] rounded-full bg-white shadow transition ${
          value ? "left-[22px]" : "left-[2px]"
        }`}
      />
    </button>
  );
}

type SegOption<T extends string> = { value: T; label: string };

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  disabled,
  nullable,
}: {
  value: T | null;
  options: readonly SegOption<T>[];
  onChange: (next: T | null) => void;
  disabled?: boolean;
  nullable?: boolean;
}) {
  const all: Array<SegOption<T | "">> = nullable
    ? [{ value: "" as T | "", label: "None" }, ...options]
    : [...options];
  return (
    <div className="flex rounded-lg bg-slate-100 p-[2px] overflow-x-auto no-scrollbar">
      {all.map((opt) => {
        const selected = opt.value === "" ? value === null : opt.value === value;
        return (
          <button
            key={opt.value || "__none"}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.value === "" ? null : (opt.value as T))}
            className={`flex-1 min-w-[52px] h-9 px-3 rounded-md text-[14px] font-medium transition ${
              selected ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            } disabled:opacity-50`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
